import { apiClient } from './api/client';

// ========== Types ==========

export interface FAQ {
  id: number;
  question: string;
  answer: string;
  category?: string;
  status: 'published' | 'draft';
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface Testimonial {
  id: number;
  customer_name?: string;
  customer_email?: string;
  rating: number;
  comment: string;
  avatar_url?: string;
  product_id?: number;
  product_name?: string;
  status: 'pending' | 'validated' | 'rejected';
  featured_on_home: boolean;
  origin: 'manual' | 'product_page' | 'form';
  created_at: string;
  updated_at: string;
}

// ========== FAQ Service ==========

export const faqService = {
  /**
   * Récupère toutes les FAQ (admin)
   */
  async getAll(filters?: { status?: string; category?: string }): Promise<FAQ[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.category) params.append('category', filters.category);

    const response = await apiClient.get<FAQ[]>(`/cms/faq${params.toString() ? `?${params}` : ''}`);
    return response;
  },

  /**
   * Récupère une FAQ par son ID
   */
  async getById(id: number): Promise<FAQ> {
    const response = await apiClient.get<FAQ>(`/cms/faq/${id}`);
    return response;
  },

  /**
   * Crée une nouvelle FAQ
   */
  async create(data: {
    question: string;
    answer: string;
    category?: string;
    status?: 'published' | 'draft';
    display_order?: number;
  }): Promise<FAQ> {
    const response = await apiClient.post<FAQ>('/cms/faq', data);
    return response;
  },

  /**
   * Met à jour une FAQ
   */
  async update(id: number, data: Partial<FAQ>): Promise<FAQ> {
    const response = await apiClient.put<FAQ>(`/cms/faq/${id}`, data);
    return response;
  },

  /**
   * Supprime une FAQ
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/cms/faq/${id}`);
  },

  /**
   * Active/Désactive une FAQ
   */
  async toggleStatus(id: number, status: 'published' | 'draft'): Promise<FAQ> {
    const response = await apiClient.put<FAQ>(`/cms/faq/${id}/toggle-status`, { status });
    return response;
  },

  /**
   * Réordonne les FAQ
   */
  async reorder(orders: Array<{ id: number; display_order: number }>): Promise<void> {
    await apiClient.put('/cms/faq/reorder', { orders });
  },

  /**
   * Récupère les FAQ publiées (pour le frontend public)
   */
  async getPublished(): Promise<FAQ[]> {
    const response = await apiClient.get<FAQ[]>('/cms/public/faq');
    return response;
  }
};

// ========== Testimonial Service ==========

export const testimonialService = {
  /**
   * Récupère tous les témoignages (admin)
   */
  async getAll(filters?: {
    status?: string;
    product_id?: number;
    featured_on_home?: boolean;
  }): Promise<Testimonial[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.product_id) params.append('product_id', filters.product_id.toString());
    if (filters?.featured_on_home !== undefined) {
      params.append('featured_on_home', filters.featured_on_home ? 'true' : 'false');
    }

    const response = await apiClient.get<Testimonial[]>(`/cms/testimonials${params.toString() ? `?${params}` : ''}`);
    return response;
  },

  /**
   * Récupère un témoignage par son ID
   */
  async getById(id: number): Promise<Testimonial> {
    const response = await apiClient.get<Testimonial>(`/cms/testimonials/${id}`);
    return response;
  },

  /**
   * Crée un nouveau témoignage
   */
  async create(data: {
    customer_name?: string;
    customer_email?: string;
    rating?: number;
    comment: string;
    avatar_url?: string;
    product_id?: number;
    status?: 'pending' | 'validated' | 'rejected';
    featured_on_home?: boolean;
    origin?: 'manual' | 'product_page' | 'form';
  }): Promise<Testimonial> {
    const response = await apiClient.post<Testimonial>('/cms/testimonials', data);
    return response;
  },

  /**
   * Met à jour un témoignage
   */
  async update(id: number, data: Partial<Testimonial>): Promise<Testimonial> {
    const response = await apiClient.put<Testimonial>(`/cms/testimonials/${id}`, data);
    return response;
  },

  /**
   * Supprime un témoignage
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/cms/testimonials/${id}`);
  },

  /**
   * Valide un témoignage
   */
  async validate(id: number): Promise<Testimonial> {
    const response = await apiClient.put<Testimonial>(`/cms/testimonials/${id}/validate`);
    return response;
  },

  /**
   * Désactive un témoignage
   */
  async deactivate(id: number): Promise<Testimonial> {
    const response = await apiClient.put<Testimonial>(`/cms/testimonials/${id}/deactivate`);
    return response;
  },

  /**
   * Met en avant ou retire de la page d'accueil
   */
  async toggleFeatured(id: number, featured: boolean): Promise<Testimonial> {
    const response = await apiClient.put<Testimonial>(`/cms/testimonials/${id}/featured`, { featured });
    return response;
  },

  /**
   * Récupère les témoignages validés (pour le frontend public)
   */
  async getValidated(options?: {
    product_id?: number;
    featured_on_home?: boolean;
  }): Promise<Testimonial[]> {
    const params = new URLSearchParams();
    if (options?.product_id) params.append('product_id', options.product_id.toString());
    if (options?.featured_on_home !== undefined) {
      params.append('featured_on_home', options.featured_on_home ? 'true' : 'false');
    }

    const response = await apiClient.get<Testimonial[]>(`/cms/public/testimonials${params.toString() ? `?${params}` : ''}`);
    return response;
  }
};
